import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {SessionStorage} from "ngx-webstorage";
import {User} from "../../../../shared/user/user";
import {Localization} from "../../../../shared/model/localization.model";
import {Range} from "../../../../shared/model/range-model";
import {
  AssociateOptionCategoryPayload,
  AssociateOptionPayload, CategoryOrderUpdatePayload,
  CubicleOption,
  EditCategoryPayload,
  OptionOrderUpdatePayload
} from "../../../../shared/model/cubicleOption-model";
import {Reference} from "../../../../configuration/shared/model/reference";
import {OptionsService} from "../../../../shared/services/options.service";
import {MessageService} from "primeng/api";
import {CustomTranslateLoader} from "../../../../shared/localization/CustomTranslateLoader";
import {TranslateService} from "@ngx-translate/core";
import {UploadService} from "../../../shared";
import {LoggerService} from "../../../../shared/logging/logger.service";
import {RangeType} from "../../../../shared/model/range-type";
import {OdmAssociateOptionModalComponent} from "../odm-associate-option-modal/odm-associate-option-modal.component";

@Component({
  standalone: false,
  selector: 'app-odm-options-component-list',
  templateUrl: './odm-options-component-list.component.html',
  styleUrls: ['./odm-options-component-list.component.scss']
})
export class OdmOptionsComponentListComponent implements OnInit {

  @SessionStorage()
  public user: User;

  @SessionStorage()
  localization: Localization;

  @Input()
  selectedRange: Range;

  @Output()
  clickBack: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('associateOptionModal') associateOptionModal: OdmAssociateOptionModalComponent;

  isLoading = false;
  isExportLoading = false;
  optionsByReference: Map<string, CubicleOption[]>;
  references: Reference[] = [];
  displayedReferences: Reference[] = [];
  activeOption: string;
  searchValue: string;
  searchPlaceholder: string;


  constructor(private readonly optionsService: OptionsService,
              private readonly messageService: MessageService,
              private readonly customTranslateLoader: CustomTranslateLoader,
              private readonly translateService: TranslateService,
              private readonly uploadService: UploadService,
              private readonly logger: LoggerService,) {
  }

  ngOnInit(): void {
    this.getOptions();
    this.updateLocalization();
    this.searchPlaceholder = this.translateService.instant("T_SEARCH");
  }

  returnOnOfferSelection() {
    this.clickBack.emit();
  }

  getOptions(showLoader: boolean = true) {
    this.isLoading = showLoader;
    this.optionsService.getOptionsByRangeId(this.selectedRange.id, true).subscribe(payload => {
      const sortedOptionsByReference = new Map<string, CubicleOption[]>();
      Object.keys(payload.optionsByReferences).sort().forEach(key => {

        payload.optionsByReferences[key] = payload.optionsByReferences[key].sort((a, b) => {
          return a.orderIndex - b.orderIndex;
        });
        // Sort values by reference
        payload.optionsByReferences[key].forEach(option =>
          option.values = option.values.sort((a, b) => {
            if (a.reference === null && b.reference === null) return 0;
            if (a.reference === null) return 1;
            if (b.reference === null) return -1;
            return a.reference.ref.localeCompare(b.reference.ref);
          })
        );
        sortedOptionsByReference.set(key, payload.optionsByReferences[key]);
      });

      this.optionsByReference = sortedOptionsByReference;
      this.references = payload.references.sort((a, b) => a.ref.localeCompare(b.ref));
      this.displayedReferences = this.references;
      if (this.searchValue && this.searchValue.trim()) {
        const search = this.searchValue.trim();
        this.displayedReferences = this.references.filter(r => r.ref && r.ref.includes(search));
      }
      this.isLoading = false;
    }, () => this.isLoading = false)
  }

  onSuccessEditingOption(message: string) {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('T_INFO'),
      detail: this.translateService.instant(message),
    });

    this.customTranslateLoader.getLastTranslation(this.user.preferredLanguage).subscribe(res => {
      this.translateService.setTranslation(this.user.preferredLanguage, res, true);
      this.getOptions();
    });
  }

  onErrorEditingOption(error: string, message: string) {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('T_ERROR'),
      detail: this.translateService.instant(message) + ': ' + error,
    });
    this.getOptions();
  }


  onClickExportOptionsButton() {
    this.logger.info('Export options of range: ' + this.translateService.instant(this.selectedRange.nameKey));
    this.isExportLoading = true;
    this.optionsService.exportOptionsOfRange(this.selectedRange.id).subscribe(
      response => {
        this.isExportLoading = false;
        this.uploadService.createXlsFileFromBackendResponse(response);
      }, () => {
        this.isExportLoading = false;
      }
    );

  }

  onEditCategoryName(event: EditCategoryPayload) {
    this.optionsService.editCategoryName({category: event, rangeId: this.selectedRange.id}).subscribe(
      () => this.onSuccessEditingOption('T_OFFER_DATA_MANAGEMENT_SUCCESS_EDITING_OPTION'),
      error => this.onErrorEditingOption(error, 'T_OFFER_DATA_MANAGEMENT_ERROR_EDITING_OPTION')
    )
  }

  onEditCategoryAssociation(event: AssociateOptionCategoryPayload) {
    this.optionsService.editAssociatedOptions(event).subscribe(result => {
        this.getOptions();
        this.onSuccessEditingOption('T_OFFER_DATA_MANAGEMENT_SUCCESS_EDITING_ASSOCIATION_OPTION')
      },
      error => this.onErrorEditingOption(error, 'T_OFFER_DATA_MANAGEMENT_ERROR_EDITING_ASSOCIATION_OPTION'));
  }

  onDeleteCategoryAssociation(event: AssociateOptionCategoryPayload) {
    this.optionsService.previewAssociatedOptions({
      options: [],
      components: [event.component],
      rangeId: this.selectedRange.id,
      category: null
    }).subscribe(components => {
      this.associateOptionModal.showDeletePreview(components, event);
    });
  }

  onDeleteCategoryConfirmed(event: AssociateOptionCategoryPayload) {
    this.optionsService.editAssociatedOptions(event).subscribe(
      () => {
        this.onSuccessEditingOption('T_OFFER_DATA_MANAGEMENT_SUCCESS_EDITING_ASSOCIATION_OPTION')
      },
      error => this.onErrorEditingOption(error, 'T_OFFER_DATA_MANAGEMENT_ERROR_EDITING_ASSOCIATION_OPTION')
    );
  }

  onClickAddAssociationOptionButton() {
    this.associateOptionModal.show()
  }

  associateOptions(payload: AssociateOptionPayload) {
    this.optionsService.addAssociatedOptions(payload).subscribe(result => {
        this.getOptions();
        this.onSuccessEditingOption('T_OFFER_DATA_MANAGEMENT_SUCCESS_EDITING_ASSOCIATION_OPTION')
      },
      error => this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('T_ERROR'),
        detail: this.translateService.instant('T_OFFER_DATA_MANAGEMENT_ERROR_EDITING_ASSOCIATION_OPTION') + ': ' + error,
      }));
  }

  searchInputChanged() {
    if (!this.references)
      return;

    this.displayedReferences = this.references.filter(reference => {
      // check ref name
      if (reference.ref.toLowerCase().includes(this.searchValue.toLowerCase()))
        return true;
      // check options
      let options: CubicleOption[] = this.optionsByReference.get(reference.ref);
      return options.some(opt => {
        // Check category
        if (opt.category !== null && this.translateService.instant(opt.category?.name).toLowerCase().includes(this.searchValue.toLowerCase())) {
          return true;
        }
        // Check option name
        if (this.translateService.instant(opt.name).toLowerCase().includes(this.searchValue.toLowerCase())) {
          return true;
        }
        // Check option values
        return opt.values.some(value => {
          // Check option value name
          if (this.translateService.instant(value.value).toLowerCase().includes(this.searchValue.toLowerCase())) {
            return true;
          }
          // Check option value ref
          if (value.reference && value.reference.ref && value.reference.ref.toLowerCase().includes(this.searchValue.toLowerCase())) {
            return true;
          }

          return false;
        });
      })
    })
  }


  updateLocalization(): void {
    const matchedLanguage = this.localization.langs.find(lang => lang.language === this.user.preferredLanguage);
    if (matchedLanguage) {
      this.customTranslateLoader.getTranslation(this.user.preferredLanguage, matchedLanguage.available).subscribe(translations => {
        this.translateService.setTranslation(this.user.preferredLanguage, translations, true);
      });
    }
  }
  onChangeCategoryOrder(payload: CategoryOrderUpdatePayload): void {
    this.activeOption = payload.componentRef;
    this.optionsService.updateCategoryOrder(payload)
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.handleCategoryOrderSuccess();
        },
        error: (error) => {
          this.isLoading = false;
          this.handleCategoryOrderError();
        }
      });
  }
  private handleCategoryOrderSuccess(): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('T_INFO'),
      detail: this.translateService.instant('T_OFFER_DATA_MANAGEMENT_SUCCESS_EDITING_OPTION'),
    });
    this.getOptions(false);
  }
  private handleCategoryOrderError(): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('T_ERROR'),
      detail: this.translateService.instant('T_OFFER_DATA_MANAGEMENT_ERROR_EDITING_OPTION')
    });
    this.getOptions(false);
  }

  onChangeOptionOrder(payload: OptionOrderUpdatePayload) {
    this.activeOption = payload.componentRef;
    this.optionsService.updateOptionsOrder(payload)
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.handleOptionsOrderSuccess();
        },
        error: (error) => {
          this.isLoading = false;
          this.handleOptionsOrderError();
        }
      });
  }
  private handleOptionsOrderSuccess(): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('T_INFO'),
      detail: this.translateService.instant('T_OFFER_DATA_MANAGEMENT_SUCCESS_EDITING_OPTION'),
    });
    this.getOptions(false);
  }
  private handleOptionsOrderError(): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('T_ERROR'),
      detail: this.translateService.instant('T_OFFER_DATA_MANAGEMENT_ERROR_EDITING_OPTION')
    });
    this.getOptions(false);
  }
}

